/**
 * Component Name: RenderTable
 * Description: A utility component that conditionally renders the appropriate table component based on the selected grouping or sub-table state.
 * Props:
 *   - subTable (object, optional): Sub-table filter data (type, id, label, projectId, stageId).
 *   - filteredInvoices (array): List of filtered invoice objects to display.
 *   - projects (array): List of project objects for mapping project IDs to names.
 *   - breadcrumbItems (array): List of breadcrumb items for navigation.
 *   - handleViewInvoiceDetailsModal (function): Callback to handle invoice detail view clicks.
 *   - selectedGroup (string): Current grouping type ('project', 'section', 'po', etc.).
 *   - handlePOClick (function): Callback to handle clicks on PO numbers.
 *   - handleBreadcrumbClick (function): Callback for handling breadcrumb clicks.
 *   - handleProjectClick (function): Callback to handle clicks on project names.
 *   - handleSectionClick (function): Callback to handle clicks on section names.
 *   - handleSupplierClick (function): Callback to handle clicks on supplier names.
 *   - handlePeriodClick (function): Callback to handle clicks on period labels.
 * Feature: Dynamically selects and renders the appropriate table (e.g., InvoiceTable, PODetailsTable, ProjectGroupedTable) based on the grouping or sub-table state, ensuring seamless navigation and data display in the invoice management interface.
 */
import PODetailsTable from "./po-details-table";
import InvoiceDetailsTable from "./invoice-details-table";
import InvoiceTable from "./invoice-table";
import MonthQuarterGroupedTable from "./month-quarter-grouped-table";
import POGroupedTable from "./po-grouped-table";
import ProjectGroupedTable from "./project-grouped-table";
import SectionGroupedTable from "./section-grouped-table";
import SupplierGroupedTable from "./supplier-grouped-table";
import { FILTER_KEYS, INVOICE_ERROR_MESSAGES, INVOICE_GROUPING_VALUES } from "src/util/enums/invoice-management";

const renderTable = ({
    subTable,
    filteredInvoices,
    projects,
    breadcrumbItems,
    handleViewInvoiceDetailsModal,
    selectedGroup,
    handlePOClick,
    handleBreadcrumbClick,
    handleProjectClick,
    handleSectionClick,
    handleSupplierClick,
    handlePeriodClick
}) => {
    try {
        // Handle sub-table rendering
        if (subTable) {
            let filterKey;
            if (subTable.type === INVOICE_GROUPING_VALUES.PO) {
                return (
                    <PODetailsTable
                        filteredInvoices={filteredInvoices}
                        stageId={subTable.stageId || ''}
                        projectId={subTable.projectId}
                        handlePOClick={handlePOClick}
                        projects={projects}
                        breadcrumbItems={breadcrumbItems}
                        handleBreadcrumbClick={handleBreadcrumbClick}
                    />
                );
            } else if (
                [
                    INVOICE_GROUPING_VALUES.SUPPLIER_INVOICES,
                    INVOICE_GROUPING_VALUES.MONTH_INVOICES,
                    INVOICE_GROUPING_VALUES.QUARTER_INVOICES,
                    INVOICE_GROUPING_VALUES.INVOICES
                ].includes(subTable.type)
            ) {
                if (subTable.type === INVOICE_GROUPING_VALUES.SUPPLIER_INVOICES) {
                    filterKey = FILTER_KEYS.SUPPLIER;
                } else if (subTable.type === INVOICE_GROUPING_VALUES.MONTH_INVOICES) {
                    filterKey = FILTER_KEYS.MONTHORPERIOD;
                } else if (subTable.type === INVOICE_GROUPING_VALUES.QUARTER_INVOICES) {
                    filterKey = FILTER_KEYS.QUARTER;
                } else if (subTable.type === INVOICE_GROUPING_VALUES.INVOICES) {
                    filterKey = FILTER_KEYS.ID;
                }

                return (
                    <InvoiceDetailsTable
                        filteredInvoices={filteredInvoices}
                        filterKey={filterKey}
                        filterValue={subTable.id}
                        handleViewDetails={handleViewInvoiceDetailsModal}
                        projects={projects}
                        breadcrumbItems={breadcrumbItems}
                        handleBreadcrumbClick={handleBreadcrumbClick}
                    />
                );
            }
        }

        // Handle grouped table rendering based on selectedGroup
        switch (selectedGroup) {
            case INVOICE_GROUPING_VALUES.PROJECT:
                return (
                    <ProjectGroupedTable
                        filteredInvoices={filteredInvoices}
                        projects={projects}
                        handleProjectClick={handleProjectClick}
                    />
                );
            case INVOICE_GROUPING_VALUES.SECTION:
                return (
                    <SectionGroupedTable
                        filteredInvoices={filteredInvoices}
                        projects={projects}
                        handleSectionClick={handleSectionClick}
                    />
                );
            case INVOICE_GROUPING_VALUES.PO:
                return (
                    <POGroupedTable
                        filteredInvoices={filteredInvoices}
                        projects={projects}
                        handlePOClick={handlePOClick}
                    />
                );
            case INVOICE_GROUPING_VALUES.SUPPLIER:
                return (
                    <SupplierGroupedTable
                        filteredInvoices={filteredInvoices}
                        handleSupplierClick={handleSupplierClick}
                    />
                );
            case INVOICE_GROUPING_VALUES.MONTH:
                return (
                    <MonthQuarterGroupedTable
                        filteredInvoices={filteredInvoices}
                        handlePeriodClick={handlePeriodClick}
                        selectedGroup={INVOICE_GROUPING_VALUES.MONTH}
                    />
                );
            case INVOICE_GROUPING_VALUES.QUARTER:
                return (
                    <MonthQuarterGroupedTable
                        filteredInvoices={filteredInvoices}
                        handlePeriodClick={handlePeriodClick}
                        selectedGroup={INVOICE_GROUPING_VALUES.QUARTER}
                    />
                );
            case INVOICE_GROUPING_VALUES.NONE:
            default:
                return (
                    <InvoiceTable
                        filteredInvoices={filteredInvoices}
                        projects={projects}
                        handleViewDetails={handleViewInvoiceDetailsModal}
                    />
                );
        }
    } catch (error) {
        console.error('Error rendering table:', error);
        return (
            <div className="alert alert-danger" role="alert">
                <strong>Error:</strong> {INVOICE_ERROR_MESSAGES.LOAD_TABLE_ERROR}
            </div>
        );
    }
};

export default renderTable;